import { useMemo, useState } from "react";
import {
  useGetPaymentsQuery,
  useGetInvoicesQuery,
  useDeletePaymentMutation,
} from "../../../../redux/api";
import type { Payment } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { PaymentFormModal } from "./payment";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 7;

const STATUS_TONE: Record<string, string> = {
  completed: "badge--success",
  pending: "badge--warning",
  failed: "badge--danger",
  refunded: "badge--info",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Inventory → Payments (list page). Create/edit happen in a popup
// (PaymentFormModal, from payment.tsx).
const Payments = () => {
  const {
    data: payments = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetPaymentsQuery();

  const { data: invoices = [] } = useGetInvoicesQuery();

  const [deletePayment] = useDeletePaymentMutation();

  const [search, setSearch] = useState("");

  const invoiceNo = (id?: string | null) =>
    id ? invoices.find((i) => i.id === id)?.invoiceNumber ?? id : "—";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = payments.find((p) => p.id === viewingId) ?? null;


  const handleDelete = async (payment: Payment) => {
    if (!window.confirm(`Delete this payment?`)) return;
    try {
      await deletePayment(payment.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete payment.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter((p: Payment) =>
      [p.paymentNumber, p.reference, p.method, p.status, invoiceNo(p.invoiceId)].some(
        (field) => (field ?? "").toLowerCase().includes(q),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, search, invoices]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load payments"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Payments"
          onBack={() => setViewingId(null)}
          title={viewing.paymentNumber || viewing.reference || viewing.id}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Invoice", value: invoiceNo(viewing.invoiceId) },
              { label: "Payment date", value: formatDate(viewing.paymentDate) },
              { label: "Amount", value: viewing.amount?.toLocaleString?.() ?? viewing.amount },
              { label: "Method", value: viewing.method },
              { label: "Reference", value: viewing.reference },
              { label: "Notes", value: viewing.notes, full: true },
            ]}
          />
        </EntityOverview>
        <PaymentFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Payments</h2>
            <p className="panel__sub">
              Showing {visible.length} of {payments.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search payments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New payment
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Invoice</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading payments…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {payments.length === 0
                      ? "No payments yet."
                      : "No payments match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((payment: Payment) => (
                  <tr key={payment.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(payment.id)}
                      >
                        {payment.paymentNumber || payment.reference || payment.id}
                      </button>
                    </td>
                    <td>{invoiceNo(payment.invoiceId)}</td>
                    <td className="cell-date">{formatDate(payment.paymentDate)}</td>
                    <td className="cell-date">
                      {payment.amount?.toLocaleString?.() ?? payment.amount}
                    </td>
                    <td>
                      <span className="pill pill--neutral">{payment.method}</span>
                    </td>
                    <td>
                      <span className={`badge ${statusTone(payment.status)}`}>
                        <span className="badge__dot" />
                        {payment.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View payment",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(payment.id),
                          },
                          {
                            label: "Edit payment",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(payment.id),
                          },
                          {
                            label: "Delete payment",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(payment),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <PaymentFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Payments;
